import React, { memo, useCallback, useId, useMemo, useRef } from 'react';

import { Stack, Typography } from '@mui/material';
import DataFieldCDS from '@sharedCR/coreDesignSystem/form/DataFieldCDS';
import ToolbarButtonGroupCDS from 'app/shared/componentsRestyled/coreDesignSystem/Buttons/ToolbarButtonGroupCDS';
import {
  PricingTargetType,
  RATE_DECIMALS,
} from 'app/shared/modules/PricingWorkbench/PricingWorkbench.constants';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { PROGRAM_FILTERS_GROUP_ID, ProgramFilterType } from 'constants/programs';
import useFilter from 'hooks/useFilter';

import { ProgramsAndPricingTestIds } from '../../../../ProgramsAndPricing.testIds';

import {
  DESIRED_TARGET_INPUT_WIDTH,
  DesiredTargetInputProps,
} from './DesiredTargetFilter.constants';
import { desiredTargetFilterStyles } from './DesiredTargetFilter.styles';
import { getTargetTypeButtons, parseDesiredTargetValue } from './DesiredTargetFilter.utils';

const DesiredTargetFilter = ({ isHidden = false, isLoading, onSearch }) => {
  const labelId = useId();
  const inputRef = useRef(null);

  const { filterValues: desiredTargetType, setFilterValue: setDesiredTargetType } = useFilter({
    filtersGroupId: PROGRAM_FILTERS_GROUP_ID,
    filterId: ProgramFilterType.DESIRED_TARGET_TYPE,
  });
  const { filterValues: desiredTargetValue, setFilterValue: setDesiredTargetValue } = useFilter({
    filtersGroupId: PROGRAM_FILTERS_GROUP_ID,
    filterId: ProgramFilterType.DESIRED_TARGET_VALUE,
  });

  const targetType = desiredTargetType ?? PricingTargetType.RATE;
  const inputProps = DesiredTargetInputProps[targetType];

  const targetTypeButtons = useMemo(
    () => getTargetTypeButtons({ isDisabled: isLoading }),
    [isLoading],
  );

  const onChangeTargetType = useCallback(
    (e, newTargetType) => {
      if (_.isNil(newTargetType)) {
        return;
      }

      setDesiredTargetType(newTargetType);
      setDesiredTargetValue(null);
      // The toolbar's program-name search captures typing whenever focus sits on a button.
      inputRef.current?.focus();
    },
    [setDesiredTargetType, setDesiredTargetValue],
  );

  const onChangeTargetValue = useCallback(
    e => setDesiredTargetValue(parseDesiredTargetValue(e.target.value)),
    [setDesiredTargetValue],
  );

  const onKeyDown = useCallback(
    e => {
      if (e.key !== 'Enter' || isLoading) {
        return;
      }

      e.preventDefault();
      onSearch();
    },
    [isLoading, onSearch],
  );

  if (isHidden) {
    return null;
  }

  return (
    <Stack aria-labelledby={labelId} role="group" sx={desiredTargetFilterStyles}>
      <Typography color="text.secondary" id={labelId} variant="regular14">
        Desired:
      </Typography>

      <ToolbarButtonGroupCDS
        exclusive
        buttons={targetTypeButtons}
        value={targetType}
        onChange={onChangeTargetType}
      />

      <DataFieldCDS
        isNumericString
        adornmentType={inputProps.adornmentType}
        ariaLabel={inputProps.ariaLabel}
        dataTestId={ProgramsAndPricingTestIds.DESIRED_TARGET_INPUT}
        decimalScale={RATE_DECIMALS}
        decimalSeparator="."
        disabled={isLoading}
        inputRef={inputRef}
        maxValue={inputProps.maxValue}
        name={ProgramFilterType.DESIRED_TARGET_VALUE}
        placeholder={inputProps.placeholder}
        type={inputProps.type}
        value={desiredTargetValue ?? ''}
        width={DESIRED_TARGET_INPUT_WIDTH}
        onChange={onChangeTargetValue}
        onKeyDown={onKeyDown}
      />
    </Stack>
  );
};

DesiredTargetFilter.propTypes = {
  isLoading: PropTypes.bool.isRequired,
  onSearch: PropTypes.func.isRequired,
  isHidden: PropTypes.bool,
};

export default memo(DesiredTargetFilter);
